import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Me, Perfil } from '@pcp/contracts';
import { useMe } from '../api/queries';
import { LoadingBlock } from '../components/states';
import { EmptyState } from '../components/states';
import { t } from '../i18n/pt-BR';

/** Ponto de entrada de cada perfil: o operador só tem `/operador`. */
export const homeFor = (perfil: Perfil) =>
  perfil === 'operator' ? '/operador' : perfil === 'importacao' || perfil === 'financeiro' ? '/importacao' : '/carga';

/** Rota forçada enquanto a senha for provisória (vale para todos os perfis, inclusive o operador). */
export const FORCED_PASSWORD_ROUTE = '/trocar-senha';

function Boot() {
  return (
    <div className="wrap" style={{ paddingBlock: 40 }}>
      <LoadingBlock />
    </div>
  );
}

/** Exige sessão. Sem sessão, vai para o login guardando de onde veio. */
function useSessionGate(): { me: Me; element: null } | { me: null; element: ReactNode } {
  const me = useMe();
  const location = useLocation();
  if (me.isLoading) return { me: null, element: <Boot /> };
  if (me.isError || !me.data) {
    return { me: null, element: <Navigate to="/login" replace state={{ from: location.pathname }} /> };
  }
  return { me: me.data, element: null };
}

/** Rotas do PCP (menu). O operador é desviado para `/operador`; senha provisória, para a troca. */
export function RequirePcpUser() {
  const gate = useSessionGate();
  if (!gate.me) return gate.element;
  if (gate.me['troca-senha-obrigatoria']) return <Navigate to={FORCED_PASSWORD_ROUTE} replace />;
  if (gate.me.perfil === 'operator') return <Navigate to="/operador" replace />;
  return <Outlet />;
}

/** Rota do operador. Os demais perfis voltam para o início. */
export function RequireOperator() {
  const gate = useSessionGate();
  if (!gate.me) return gate.element;
  if (gate.me['troca-senha-obrigatoria']) return <Navigate to={FORCED_PASSWORD_ROUTE} replace />;
  if (gate.me.perfil !== 'operator') return <Navigate to={homeFor(gate.me.perfil)} replace />;
  return <Outlet />;
}

/** Qualquer pessoa autenticada (usada pela troca de senha obrigatória). */
export function RequireSession() {
  const gate = useSessionGate();
  if (!gate.me) return gate.element;
  return <Outlet />;
}

/** Restringe uma tela a certos perfis; os demais veem "Sem acesso" (o BFF também recusa). */
export function RoleGate({ roles, children }: { roles: readonly Perfil[]; children: ReactNode }) {
  const me = useMe();
  if (!me.data) return null;
  if (!roles.includes(me.data.perfil)) {
    return <EmptyState title={t.states.forbiddenTitle}>{t.states.forbidden}</EmptyState>;
  }
  return <>{children}</>;
}

/** Quem já tem sessão não precisa ver o login. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.isLoading) return <Boot />;
  if (me.data) {
    const to = me.data['troca-senha-obrigatoria'] ? FORCED_PASSWORD_ROUTE : homeFor(me.data.perfil);
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}
